import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LayoutDashboard, LogOut } from 'lucide-react';
import { cn } from '../lib/utils';
import { Player } from '../types';

interface ScorePanelProps {
  players: Player[];
  currentPlayerIndex: number;
  onRestart: () => void;
  onExit?: () => void;
  isOnline?: boolean;
  isConnected?: boolean;
  targetScore?: number;
}

export default React.memo(function ScorePanel({ players, currentPlayerIndex, onRestart, onExit, isOnline, isConnected, targetScore }: ScorePanelProps) {
  const [showAbandonConfirm, setShowAbandonConfirm] = React.useState(false);

  return (
    <>
      <header className="w-full max-w-4xl flex flex-col items-center mb-0.5 pt-1" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      {/* Score Panel */}
      <div className="w-full flex items-center justify-center gap-1 glass-panel p-1 px-2 rounded-xl">
        {(() => {
          const isFourPlayers = players.length === 4;
          const renderedBlocks = isFourPlayers 
            ? [
                {
                  id: 'team-1',
                  names: [players[0].name, players[2].name],
                  score: players[0].totalScore,
                  isCurrent: currentPlayerIndex === 0 || currentPlayerIndex === 2,
                  originalIndices: [0, 2]
                },
                {
                  id: 'team-2',
                  names: [players[1].name, players[3].name],
                  score: players[1].totalScore,
                  isCurrent: currentPlayerIndex === 1 || currentPlayerIndex === 3,
                  originalIndices: [1, 3]
                }
              ]
            : players.map((p, idx) => ({
                id: p.id.toString(),
                names: [p.name],
                score: p.totalScore,
                isCurrent: currentPlayerIndex === idx,
                originalIndices: [idx]
              }));

          const maxScore = Math.max(...renderedBlocks.map(b => b.score));

          return renderedBlocks.map((block) => {
            const isLeader = block.score === maxScore && block.score > 0;
            const isCurrent = block.isCurrent;
            return (
              <div
                key={block.id}
                className={cn(
                  "flex flex-col items-center px-3 py-0.5 rounded-lg transition-all duration-300",
                  isFourPlayers ? "min-w-[120px] sm:min-w-[160px]" : "min-w-[60px] sm:min-w-[80px]",
                  isCurrent ? "glass-panel-amber active-glow-panel scale-105" : "bg-transparent",
                  isLeader && block.score > 0 && !isCurrent ? "ring-1 ring-amber-400/20" : ""
                )}
              >
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  {block.names.map((name, nIdx) => {
                    const globalIdx = block.originalIndices[nIdx];
                    const isPlayerCurrent = currentPlayerIndex === globalIdx;
                    return (
                      <React.Fragment key={globalIdx}>
                        {nIdx > 0 && <span className="text-white/20 text-[9px] sm:text-[10px]">&</span>}
                        <div className="flex items-center gap-1 shrink-0">
                          {isPlayerCurrent && (
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse shrink-0" />
                          )}
                          <span className={cn(
                            "text-[9px] sm:text-[10px] uppercase tracking-widest font-black transition-colors duration-300",
                            isPlayerCurrent ? "text-yellow-400" : isLeader ? "text-amber-400/80" : "text-white/40"
                          )}>
                            {name}
                          </span>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
                <span className={cn(
                  "text-xl sm:text-2xl font-bold font-serif leading-none text-center mt-0.5",
                  isLeader && block.score > 0 ? "text-amber-300" : "text-white"
                )}>
                  {block.score}
                </span>
              </div>
            );
          });
        })()}
      </div>

      {/* Title Row */}
      <div className="flex items-center gap-3 mt-0.5">
        <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-white/30" />
          <h1
          className="text-base sm:text-2xl font-black uppercase tracking-[0.2em] text-yellow-400 flex items-center gap-2"
          style={{ fontFamily: '"Playfair Display", serif', textShadow: '0 0 20px rgba(251,191,36,0.25)' }}
        >
          SBARAZZINO
        </h1>
        {targetScore && (
          <span className="text-[9px] sm:text-[10px] bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
            Traguardo: {targetScore}
          </span>
        )}
        <div className="flex items-center gap-2">
          {isOnline && (
            <span className="relative flex h-2 w-2" title={isConnected ? "Connesso" : "Disconnesso"}>
              {isConnected ? (
                <>
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </>
              ) : (
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              )}
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowAbandonConfirm(true)}
            className="cursor-pointer text-white/50 hover:text-white hover:scale-110 transition-all focus:outline-none"
            title={isOnline ? "Abbandona la partita" : "Torna al menu"}
          >
            <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>
    </header>

    <AnimatePresence>
      {showAbandonConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, y: 15 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 15 }}
            className="glass-panel p-6 rounded-3xl max-w-sm w-full text-center space-y-5 border border-white/10 shadow-2xl relative overflow-hidden"
          >
            {/* Background glow design */}
            <div className="absolute -right-12 -top-12 w-24 h-24 bg-red-500/10 rounded-full blur-xl pointer-events-none" />

            <div className="space-y-2">
              <h3 className="text-xl font-black text-yellow-400 uppercase tracking-wide" style={{ fontFamily: '"Playfair Display", serif' }}>
                {isOnline ? "Abbandonare?" : "Torna al Menu?"}
              </h3>
              <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                {isOnline 
                  ? "Se esci ora perderai la partita a tavolino. Sei sicuro di voler abbandonare?"
                  : "I progressi di questa partita andranno persi. Sei sicuro di voler tornare al menu principale?"}
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowAbandonConfirm(false);
                  if (isOnline) {
                    onRestart();
                  } else {
                    onExit?.();
                  }
                }}
                className="flex-1 py-3 bg-red-600 hover:bg-red-500 rounded-xl text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md active:scale-95"
              >
                {isOnline ? "Sì, abbandona" : "Sì, esci"}
              </button>
              <button
                onClick={() => setShowAbandonConfirm(false)}
                className="flex-1 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/80 text-xs font-bold uppercase tracking-wider transition-all active:scale-95"
              >
                Annulla
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </>
  );
});
